import type { LinksFunction, MetaFunction } from 'react-router';
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
} from 'react-router';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { CallToAction } from '@/components/CallToAction';
import { Footer } from '@/components/Footer';
import { Header } from '@/components/Header';
import { pageMeta, SITE_NAME } from '@/lib/seo';
import NotFoundPage from '@/routes/not-found/NotFoundPage';
import { readCache } from './lib/redis.server';
import '@/styles/global.css';

export const links: LinksFunction = () => [
  { rel: 'icon', type: 'image/x-icon', href: '/favicon.ico' },
  { rel: 'apple-touch-icon', href: '/logo.png' },
];

// Fallback for routes without their own meta; each page route sets its own.
export const meta: MetaFunction = ({ location }) =>
  pageMeta({ title: SITE_NAME, path: location.pathname });

export async function loader() {
  const contactInfo = await readCache('contact-info');
  return { contactInfo };
}

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="mn">
      <head>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content="#14532d" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function Root() {
  const { contactInfo } = useLoaderData<typeof loader>();

  return (
    <>
      <ToastContainer />
      {contactInfo && (
        <div>
          <Header contact={contactInfo} />
          <main>
            <Outlet />
          </main>
          <CallToAction contact={contactInfo} />
          <Footer contact={contactInfo} />
        </div>
      )}
    </>
  );
}

// A loader that throws 404 (missing blog post or competition) lands here with
// the 404 status intact, so search engines drop the URL.
export function ErrorBoundary() {
  const error = useRouteError();
  if (isRouteErrorResponse(error) && error.status === 404) return <NotFoundPage />;
  return (
    <section style={{ textAlign: 'center', paddingTop: '4rem' }}>
      <p style={{ fontSize: '1.125rem' }}>Алдаа гарлаа. Түр хүлээгээд дахин оролдоно уу.</p>
    </section>
  );
}
